import { formatOffsetDateTime } from "@freightsimple/time/helpers";
import type { OffsetDateTime } from "@freightsimple/time/branded-types";

export type TestStatus =
  | "pending"
  | "running"
  | "passed"
  | "failed"
  | "warning";

export type OverallStatus = "ok" | "issues" | "critical";

export interface TestResult {
  name: string;
  status: TestStatus;
  duration?: number | undefined;
  error?: string | undefined;
  details?: string | undefined;
}

export interface ConnectivityTestResults {
  timestamp: OffsetDateTime;
  browser: string;
  location?: string | undefined;
  environment: string;
  tests: TestResult[];
  overallStatus: OverallStatus;
}

/** Signals a single test's progress back to the screen's results state. */
export type UpdateTest = (index: number, updates: Partial<TestResult>) => void;

/**
 * The test roster in run order. The Core Endpoints test only runs when the
 * user is signed in — it hits authenticated APIs.
 */
export function buildInitialTests(isAuthenticated: boolean): TestResult[] {
  return [
    { name: "Domain Response Time", status: "pending" },
    { name: "API Connectivity", status: "pending" },
    ...(isAuthenticated
      ? [{ name: "Core Endpoints", status: "pending" as const }]
      : []),
    { name: "External Services", status: "pending" },
    { name: "Regional Connectivity", status: "pending" },
    { name: "DNS Resolution", status: "pending" },
    { name: "FreightSimple Infrastructure", status: "pending" },
    { name: "Network Path Analysis", status: "pending" },
    { name: "Static Assets", status: "pending" },
    { name: "Browser Environment", status: "pending" },
  ];
}

export function overallStatusText(status: OverallStatus): string {
  switch (status) {
    case "ok":
      return "Everything OK";
    case "issues":
      return "Issues Detected";
    case "critical":
      return "Critical Issues";
  }
}

export function overallStatusBannerVariant(
  status: OverallStatus,
): "success" | "warning" | "danger" {
  switch (status) {
    case "ok":
      return "success";
    case "issues":
      return "warning";
    case "critical":
      return "danger";
  }
}

const COPY_STATUS_EMOJI: Record<TestStatus, string> = {
  passed: "✅",
  failed: "❌",
  warning: "⚠️",
  running: "🔄",
  pending: "⏳",
};

/**
 * Renders the results as a plain-text block the customer can paste into a
 * support chat. Emoji are intentional here — this is copyable text, not UI.
 */
export function formatResultsForCopy(results: ConnectivityTestResults): string {
  const testLines = results.tests
    .map((test) => {
      const icon = COPY_STATUS_EMOJI[test.status];
      const duration = test.duration ? ` (${test.duration}ms)` : "";
      const details = test.details ? ` - ${test.details}` : "";
      return `${icon} ${test.name}${duration}${details}`;
    })
    .join("\n");

  const recommendation =
    results.overallStatus !== "ok"
      ? "\nRecommendation: Check network connectivity and contact support if issues persist."
      : "";

  return `FreightSimple Dashboard Connectivity Test Results
Generated: ${formatOffsetDateTime(results.timestamp)}
Browser: ${results.browser}
Environment: ${results.environment}
Location: ${results.location || "Not detected"}

${testLines}

Overall Status: ${overallStatusText(results.overallStatus)}${recommendation}`;
}
